<template>
  <div>

    <div class="d-flex">
      <div
        v-if="pageInfo"
        class="flex-grow-1"
      >
        <h3>
          <feather-icon
            v-if="pageInfo.icon"
            :icon="pageInfo.icon"
            size="20"
          />
          {{ pageInfo.title }}
        </h3>
      </div>
      <div class="pb-2">

        <b-dropdown
          variant="link"
          no-caret
          right
          class="chart-dropdown d-md-none"
          toggle-class="p-0"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="18"
              class="text-body cursor-pointer"
            />
          </template>

          <b-dropdown-item
            v-if="(isPermittedLocal('aiSummary') || isPermittedLocal('aiChat')) && pageInfo && pageInfo.aiActive"
            :title="$t('AI Comments')"
            @click="aiToggle = !aiToggle"
          >
            <feather-icon
              icon="CodesandboxIcon"
              size="20"
            />
            <span class="itemlabel">{{ $t('AI Comments') }}</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="isPermittedLocal('addBox') && pageInfo && pageInfo.boxCount < maxBoxCount"
            :title="$t('Add Box')"
            @click="addBox"
          >
            <feather-icon
              icon="PlusSquareIcon"
              size="20"
            />
            <span class="itemlabel">{{ $t('Add Box') }}</span>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="pageInfo && pageInfo.boxCount >= maxBoxCount"
            :title="$t('Add Box')"
            disabled
            class="text-muted"
            @click="noBox"
          >
            <feather-icon
              icon="PlusSquareIcon"
              size="20"
            />
            <span class="itemlabel">{{ $t('Add Box') }}</span>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="isPermittedLocal('setLayout')"
            @click="setLayout"
          >
            <feather-icon
              icon="ShuffleIcon"
              size="20"
            />
            <span class="itemlabel">{{ $t('Set Layout') }}</span>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="isPermittedLocal('editPage') && !myDashboard"
            @click="editPage"
          >
            <feather-icon
              icon="EditIcon"
              size="20"
            />
            <span class="itemlabel">{{ $t('Edit Page') }}</span>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="isPermittedLocal('exportPage')"
            @click="downloadPage"
          >
            <feather-icon
              icon="DownloadIcon"
              size="20"
            />
            <span class="itemlabel">{{ $t('Download Page') }} <sup
              v-if="totalDownloads > 0 && downloadCount < totalDownloads"
            >{{
              `${downloadCount}/${totalDownloads}` }}</sup></span>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="isPermittedLocal('exportPagePdf') && boxes && boxes.length && pageInfo.pdfUserGroups.length"
            @click="exportPdf"
          >
            <feather-icon
              icon="SendIcon"
              size="20"
            />
            <span class="itemlabel">{{ $t('Get Page As Pdf') }}</span>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="isPermittedLocal('deletePage') && !myDashboard"
            @click="deletePageConfirm"
          >
            <feather-icon
              icon="TrashIcon"
              size="20"
            />
            <span class="itemlabel">{{ $t('Delete Page') }}</span>
          </b-dropdown-item>

        </b-dropdown>

        <ul class="d-none d-md-block list-inline mb-0">
          <li
            v-if="(isPermittedLocal('aiSummary') || isPermittedLocal('aiChat')) && pageInfo && pageInfo.aiActive"
            :title="$t('AI Comments')"
            class="mr-2"
          >
            <a
              data-action="aiComments"
              @click="aiToggle = !aiToggle"
            >
              <feather-icon
                icon="CodesandboxIcon"
                size="20"
              />
            </a>
          </li>

          <li
            v-if="isPermittedLocal('addBox') && pageInfo && pageInfo.boxCount < maxBoxCount"
            :title="$t('Add Box')"
            class="mr-2"
          >
            <a
              data-action="addBox"
              @click="addBox"
            >
              <feather-icon
                icon="PlusSquareIcon"
                size="20"
              />
            </a>
          </li>
          <li
            v-if="pageInfo && pageInfo.boxCount >= maxBoxCount"
            :title="$t('Add Box')"
            class="mr-2 text-muted"
          >
            <a
              data-action="noBox"
              @click="noBox"
            >
              <feather-icon
                icon="PlusSquareIcon"
                size="20"
              />
            </a>
          </li>
          <li
            v-if="isPermittedLocal('setLayout')"
            :title="$t('Set Layout')"
            class="mr-2"
          >
            <a
              data-action="setLayout"
              @click="setLayout"
            >
              <feather-icon
                icon="ShuffleIcon"
                size="20"
              />
            </a>
          </li>
          <li
            v-if="isPermittedLocal('editPage') && !myDashboard"
            :title="$t('Edit Page')"
            class="mr-2"
          >
            <a
              data-action="edit"
              @click="editPage"
            >
              <feather-icon
                icon="EditIcon"
                size="20"
              />
            </a>
          </li>
          <li
            v-if="isPermittedLocal('exportPage') && boxes && boxes.length"
            :title="$t('Download Page')"
            class="mr-2"
          >
            <a
              data-action="download"
              @click="downloadPage"
            >
              <feather-icon
                icon="DownloadIcon"
                size="20"
              /> <sup
                v-if="totalDownloads > 0 && downloadCount < totalDownloads"
              >{{
                `${downloadCount}/${totalDownloads}` }}</sup>
            </a>
          </li>
          <li
            v-if="isPermittedLocal('exportPagePdf') && boxes && boxes.length && pageInfo.pdfUserGroups.length"
            :title="$t('Get Page As Pdf')"
            class="mr-2"
          >
            <a
              data-action="exportPdf"
              @click="exportPdf"
            >
              <feather-icon
                icon="SendIcon"
                size="20"
              />
            </a>
          </li>
          <li
            v-if="isPermittedLocal('deletePage') && !myDashboard"
            :title="$t('Delete Page')"
            class="mr-2"
          >
            <a
              data-action="delete"
              @click="deletePageConfirm"
            >
              <feather-icon
                icon="TrashIcon"
                size="20"
              />
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div
      v-if="pageInfo && pageInfo.description"
      class="p-2"
    >
      <h6>{{ pageInfo.description }}</h6>
    </div>

    <div
      v-if="pageInfo && pageInfo.aiActive"
      v-show="aiToggle"
      class="ai-sticky"
    >
      <b-card
        id="comments"
        ref="comments"
        class="mb-2"
      >
        <div class="d-flex justify-content-between">
          <h3>{{ $t('Comments') }}</h3>
          <div class="d-flex">
            <button
              class="btn btn-sm"
              :title="$t('Close')"
              @click="aiToggle = !aiToggle"
            >
              <feather-icon
                icon="MinusSquareIcon"
                size="16"
              />
            </button>
          </div>
        </div>

        <AIChat :page-id="pageId" />

      </b-card>
    </div>

    <box
      v-for="box in boxes"
      :id="'box_' + box.id"
      :ref="'box_' + box.id"
      :key="box.id"
      :xbox="box"
      :currently-in-my-dashboard="myDashboard"
      @edit="editBox"
      @delete="confirmDelete"
      @publish="confirmPublish"
      @clone="cloneBox"
      @clone_to_my_dashboard="cloneBoxToMyDashboard"
      @drill="addDrill(box)"
      @download="downloadBox"
      @search="searchBox"
      @drilling="drilling"
      @done="boxDone(box)"
      @running="running"
    />

    <modal-box
      v-if="pageId"
      :page-id="pageId"
      :edit-item="editItem"
      @submit="boxUpdated"
      @drillAdded="drillAdded"
    />
    <modal-search-box
      :box="searchItem"
      @submit="boxSearch"
    />
    <modal-box-layout
      :key="'modalBoxLayout'"
      :boxes="boxes"
      @submit="saveBoxLayout"
    />
    <modal-item
      item-type="page"
      :modal-name="'-editPage'"
      :edit-item="pageInfo"
      @submit="pageUpdated"
    />

  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import Box from '@/layouts/components/Box.vue'
import ModalBox from '@/layouts/components/ModalBox.vue'
import ModalSearchBox from '@/layouts/components/ModalSearchBox.vue'
import ModalBoxLayout from '@/layouts/components/ModalBoxLayout.vue'
import { getUserData, isPermitted } from '@/auth/utils'
import ModalItem from '@/layouts/components/ModalItem.vue'
import AIChat from '@/layouts/components/AIChat.vue'
import html2canvas from 'html2canvas'
import {
  BDropdown,
  BDropdownItem,
  BCard,
} from 'bootstrap-vue'

export default {
  components: {
    Box,
    ModalBox,
    ModalSearchBox,
    ModalBoxLayout,
    ModalItem,
    BDropdown,
    BDropdownItem,
    BCard,
    AIChat,
  },
  data() {
    return {
      maxBoxCount: 40,
      pageId: this.$route.params.id,
      pageInfo: null,
      boxes: [],
      downloadCount: 0, // Add this for the counter
      totalDownloads: 0, // Track total boxes
      downloadInProgress: false,
      editItem: null,
      searchItem: null,
      boxRequest: null,
      userData: null,
      boxesToRunCount: 0,
      boxesDoneCount: 0,
      aiCommentInProgress: false,
      aiComments: null,
      aiToggle: false,
    }
  },
  computed: {
    myDashboard() {
      if (this.pageInfo && this.pageInfo.userId) {
        return true
      }
      return false
    },
  },
  watch: {
    aiToggle() {
      // Trigger resize so Highcharts reflows to new container width
      this.$nextTick(() => {
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'))
        }, 300)
      })
    },
    $route(val) {
      this.pageId = val.params.id
      this.getPageInfo()
      this.aiCommentInProgress = false
      this.aiComments = null
    },
  },
  created() {
    // this.getBoxes('created')
    this.getPageInfo()
    this.userData = getUserData()
  },
  methods: {
    running() {
      this.comments = null
      this.aiCommentInProgress = true
      this.boxesToRunCount += 1
    },
    boxDone() {
      if (this.downloadInProgress || (!this.isPermittedLocal('aiSummary') && !this.isPermittedLocal('aiChat'))) {
        // Still notify the AI panel that boxes are refreshing, even if the legacy
        // aiComments flow is disabled — the AIChat summary should auto re-run.
        this.$root.$emit('aiBoxesRefreshed')
        return
      }
      this.boxesDoneCount += 1
      if (this.boxesDoneCount === this.boxesToRunCount) {
        // All boxes finished refreshing — let the AI panel know so summary can re-run.
        this.$root.$emit('aiBoxesRefreshed')
        // wait 2 seconds
        setTimeout(() => {
          this.boxesDoneCount = 0
          this.boxesToRunCount = 0
        }, 2000)
      }
    },
    noBox() {
      this.$swal({
        title: this.$t('Warning'),
        text: this.$t('This page has reached box limit!'),
        icon: 'warning',
        showCancelButton: false,
        confirmButtonText: this.$t('OK'),
        customClass: {
          confirmButton: 'btn btn-primary',
        },
        buttonsStyling: false,
      }).then(() => {
      })
    },
    drilling(args, rootId) {
      const boxIndex = this.boxes.findIndex(b => b.id === rootId)
      this.boxes[boxIndex] = args
      this.$root.$emit(`boxDrilled-${rootId}`, args.id)
    },
    async boxUpdated(id) {
      await this.getBoxes('boxUpdated')
      this.$root.$emit(`boxUpdated-${id}`)
    },
    async drillAdded(id) {
      this.$root.$emit(`boxDrillAdded-${id}`)
    },
    downloadPage() {
      this.downloadCount = 0 // Reset counter
      this.totalDownloads = this.boxes.length // Total number of downloads
      this.downloadInProgress = true
      this.boxes.forEach((element, index) => {
        setTimeout(() => {
          this.downloadBox(element, true)
          this.downloadCount += 1 // Increment counter
          if (this.downloadCount === this.totalDownloads) {
            this.downloadInProgress = false
          }
        }, index * 2000) // Ensure sequential execution
      })
    },
    editPage() {
      this.$bvModal.show('add-item-editPage')
    },
    pageUpdated() {
      this.$bvModal.hide('add-item-editPage')
      this.$root.$emit('refreshMenu')
      this.getPageInfo()
    },
    isPermittedLocal(code) {
      // skip check if pdf export
      if (!this.userData) {
        return false
      }
      return isPermitted(this.userData.role, code, this.userData.userGroups)
    },
    confirmDelete(args) {
      this.$swal({
        title: this.$t('Are you sure?'),
        text: this.$t('You are about to delete a box...'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Yes, delete it!'),
        cancelButtonText: this.$t('Cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deleteBox(args)
        }
      })
    },
    deleteBox(args) {
      let boxIndex = -1
      if (!args.drilledBy) {
        boxIndex = this.boxes.indexOf(this.boxes.find(x => x.id === args.id))
      }
      useJwt.axiosIns.delete(`box/${args.id}`)
        .then(response => {
          if (response.data) {
            // this.getBoxes()
            if (boxIndex > -1) {
              this.boxes.splice(boxIndex, 1)
            }

            if (args.drilledBy) {
              this.$root.$emit(`boxDrillDeleted-${args.drilledBy}`)
            }
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    confirmPublish(args) {
      this.$swal({
        title: this.$t('Are you sure?'),
        text: this.$t('You are about to publish a box...'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Yes, publish it!'),
        cancelButtonText: this.$t('Cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.publishBox(args)
        }
      })
    },
    publishBox(args) {
      useJwt.axiosIns.patch(`box/publish/${args.id}`)
        .then(response => {
          if (response.data) {
            this.$root.$emit(`boxUpdated-${args.id}`)
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    editBox(args) {
      this.editItem = JSON.parse(JSON.stringify(args))
      this.$bvModal.show('modal-box')
    },
    addBox() {
      this.editItem = null
      this.$bvModal.show('modal-box')
    },
    setLayout() {
      this.$bvModal.show('modal-box-layout')
    },
    saveBoxLayout(orderedList) {
      this.$bvModal.hide('modal-box-layout')
      useJwt.axiosIns.post('box/reorderboxes', { StringValue: orderedList.join(',') })
        .then(response => {
          if (response.data) {
            this.getBoxes('saveBoxLayout')
            // for refreshing boxes...
            // this.$root.$emit('filterApplied')
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    getPageInfo() {
      if (this.pageId) {
        useJwt.axiosIns.get(`page/${this.pageId}`)
          .then(response => {
            if (response.data) {
              this.pageInfo = response.data
              if (this.pageInfo.pdfAttachments) {
                this.pageInfo.pdfAttachments = JSON.parse(this.pageInfo.pdfAttachments)
              }
              this.getBoxes()
            }
          })
          .catch(e => {
            console.log(e)
          })
      }
    },
    checkUserGroupsForBox(box) {
      if (box.userGroups && box.userGroups.length) {
        let found = false
        box.userGroups.forEach(ug => {
          if (this.userData.userGroups.find(uug => uug.id === ug)) {
            found = true
          }
        })
        return found
      }
      return true
    },
    getBoxes() {
      if (this.pageId) {
        this.boxRequest = this.$store.state.globals.globalFilter
        if (this.boxRequest && this.boxRequest.fromWave > 0 && this.boxRequest.toWave > 0) {
          this.aiCommentInProgress = true
          useJwt.axiosIns.get(`box/${this.pageId}`)
            .then(response => {
              if (response.data) {
                this.boxes = response.data.filter(box => this.checkUserGroupsForBox(box))

                this.boxes.forEach(element => {
                  const newElem = element
                  newElem.boxConfig = JSON.parse(element.configJson)
                })
              }
            })
            .catch(e => {
              console.log(e)
            })
        } else {
          setTimeout(() => {
            this.getBoxes('setTimeout')
          }, 100)
        }
      } else {
        this.$router.push({ name: 'auth-login' })
      }
    },
    cloneBoxToMyDashboard(args) {
      useJwt.axiosIns.get(`box/CloneToMyDashboard/${args.id}`)
        .then(response => {
          if (response.data) {
            this.$swal({
              icon: 'success',
              title: 'Copy To My Dashboard',
              text: 'Box copied to your dashboard!',
              customClass: {
                confirmButton: 'btn btn-success',
              },
            })
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    cloneBox(args) {
      this.editItem = JSON.parse(JSON.stringify(args))
      this.editItem.id = 0
      this.editItem.drilledBy = 0
      this.editItem.boxTitle = `Copy of ${args.boxTitle}`
      this.editItem.orderNumber = args.orderNumber + 1
      this.$bvModal.show('modal-box')
    },
    addDrill(box) {
      this.editItem = JSON.parse(JSON.stringify(box))
      this.editItem.id = 0
      this.editItem.drilledBy = box.id
      this.$bvModal.show('modal-box')
    },
    searchBox(box) {
      this.searchItem = JSON.parse(JSON.stringify(box))
      this.$bvModal.show('modal-search-box')
    },
    boxSearch(args) {
      this.$root.$emit(`boxSearch-${args.boxId}`, args)
    },
    downloadBox(args, skipSelection = false) {
      let boxId = args.id
      if (args.drilledBy) {
        boxId = args.drilledBy
      }
      const boxConfig = args.boxConfig ? args.boxConfig : JSON.parse(args.configJson)
      if (boxConfig.display === 'table' && (['standard', 'score', 'frequency', 'data', 'external', 'custom'].find(x => x === boxConfig.type))) {
        this.$refs[`box_${boxId}`][0].download()
      } else if (skipSelection) {
        this.graphicDownload(args, boxId)
      } else {
        this.downloadSelect(args, boxId)
      }
    },
    downloadSelect(args, boxId) {
      this.$swal({
        title: this.$t('Please select download type'),
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('Excel'),
        cancelButtonText: this.$t('Graphic'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-primary ml-2',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$refs[`box_${boxId}`][0].download()
        } else {
          this.graphicDownload(args, boxId)
        }
      })
    },
    graphicDownload(args, boxId) {
      html2canvas(document.getElementById(`box_${boxId}`)).then(canvas => {
        const data = canvas.toDataURL()
        const link = document.createElement('a')
        link.setAttribute('href', data)
        link.setAttribute('download', `${args.boxTitle}.png`)
        link.click()
      })
    },
    deletePageConfirm() {
      this.$swal({
        title: this.$t('Are you sure?'),
        text: this.$t('You are about to delete this page...'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Yes, delete it!'),
        cancelButtonText: this.$t('Cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deletePage()
        }
      })
    },
    deletePage() {
      useJwt.axiosIns.delete(`page/${this.pageInfo.id}`)
        .then(response => {
          if (response.data) {
            this.$root.$emit('refreshMenu', true)
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    exportPdf() {
      this.$swal({
        icon: 'success',
        title: this.$t('PDF Export'),
        text: this.$t('The PDF version of the page will be sent to your email address!'),
        customClass: {
          confirmButton: 'btn btn-success',
        },
      })

      useJwt.axiosIns.get(`page/SendPdfToCurrentUser/${this.pageInfo.id}`)
        .then(response => {
          console.log('DONE', response.data)
        })
        .catch(e => {
          console.log(e)
        })
    },
  },
}
</script>

<style>
.itemlabel {
  vertical-align: middle;
  margin-left: 8px;
}

ul {
  margin-bottom: 15px !important;
}

.download-counter {
  position: fixed;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px;
  border-radius: 5px;
  font-size: 16px;
}

.ai-sticky {
  position: sticky;
  top: 72px;     /* keep below the fixed navbar */
  z-index: 100;  /* stay above the boxes below */
}

/* Reduce the AI panel padding so it matches the boxes below */
.ai-sticky .card-body {
  padding: 1rem;
}

/* Keep the AI panel compact so it doesn't cover the boxes below.
   Override the AIChat internal wrapper height and let it scroll internally. */
.ai-sticky .chat-wrapper {
  min-height: unset;
  max-height: 340px;
}
</style>
